import React from "react";
import { Helmet } from "react-helmet-async";
import { API_BASE_URL } from "../utils/api";

export const SEOHead = ({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  article = null,
  siteName = "Red Arauca",
  locale = "es_CO",
  twitterCard = "summary_large_image",
  noindex = false,
  nofollow = false,
  canonicalUrl = null,
  author = null,
}) => {
  // Construir título completo
  const fullTitle = title ? `${title} | ${siteName}` : siteName;

  // Procesar URL de imagen
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) {
      return `${window.location.origin}/assets/default-og-image.jpg`;
    }
    
    // Si ya es una URL completa
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // Si empieza con /
    if (imageUrl.startsWith('/')) {
      // Si empieza con /uploads, usar API_BASE_URL
      if (imageUrl.startsWith('/uploads/')) {
        return `${API_BASE_URL}${imageUrl}`;
      }
      // Otros paths absolutos usar el origen actual
      return `${window.location.origin}${imageUrl}`;
    }
    
    // Paths relativos
    return `${API_BASE_URL}/uploads/${imageUrl}`;
  };

  const fullImageUrl = getFullImageUrl(image);
  
  // URL canónica
  const canonical = canonicalUrl || url || window.location.href;
  
  // URL actual completa
  const currentUrl = url || window.location.href;

  // Robots meta
  const robotsContent = [];
  if (noindex) robotsContent.push("noindex");
  if (nofollow) robotsContent.push("nofollow");
  const robots = robotsContent.length > 0 ? robotsContent.join(", ") : "index, follow";

  // Description con fallback
  const metaDescription = description || `Mantente informado con las últimas noticias de Arauca y Colombia en ${siteName}. Tu fuente confiable de información regional.`;

  // Keywords con fallback
  const metaKeywords = keywords || "noticias, Arauca, Colombia, información, periodismo, medios comunicación";

  return (
    <Helmet>
      {/* Título */}
      <title>{fullTitle}</title>

      {/* Meta tags básicos */}
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={robots} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content="es" />
      <meta name="language" content="Spanish" />
      
      {/* Autor si está disponible */}
      {author && <meta name="author" content={author} />}

      {/* URL canónica */}
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title || siteName} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:secure_url" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || siteName} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title || siteName} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={title || siteName} />
      <meta name="twitter:url" content={currentUrl} />
      
      {/* Twitter adicional */}
      <meta name="twitter:creator" content="@redarauca" />
      <meta name="twitter:site" content="@redarauca" />

      {/* Artículo específico (para posts) */}
      {article && type === 'article' && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
          
          {/* Adicionales para artículos */}
          <meta property="og:type" content="article" />
          {article.readingTime && (
            <meta name="twitter:label1" content="Tiempo de lectura" />
          )}
          {article.readingTime && (
            <meta name="twitter:data1" content={`${article.readingTime} min`} />
          )}
        </>
      )}

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(generateSchema({
          type,
          title: title || siteName,
          description: metaDescription,
          image: fullImageUrl,
          url: currentUrl,
          siteName,
          article,
          author
        }))}
      </script>
      
      {/* Adicionales para SEO */}
      <meta name="theme-color" content="#3b82f6" />
      <meta name="msapplication-TileColor" content="#3b82f6" />
      <meta name="application-name" content={siteName} />
    </Helmet>
  );
};

// Función para generar Schema.org
const generateSchema = ({ type, title, description, image, url, siteName, article, author }) => {
  const baseUrl = API_BASE_URL.replace('/api', '');
  
  if (type === "article" && article) {
    return {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: title,
      description: description,
      image: {
        "@type": "ImageObject",
        url: image,
        width: 1200,
        height: 630
      },
      datePublished: article.publishedTime,
      dateModified: article.modifiedTime || article.publishedTime,
      author: {
        "@type": "Person",
        name: article.author || author || siteName
      },
      publisher: {
        "@type": "Organization",
        name: siteName,
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/assets/logo.png`,
          width: 200,
          height: 60
        }
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url
      },
      articleSection: article.section,
      keywords: article.tags ? article.tags.join(', ') : '',
      wordCount: article.wordCount,
      ...(article.readingTime && { timeRequired: `PT${article.readingTime}M` })
    };
  }

  // Schema para website o páginas generales
  return {
    "@context": "https://schema.org",
    "@type": type === "website" ? "WebSite" : "WebPage",
    name: title,
    description: description,
    url: url,
    image: {
      "@type": "ImageObject",
      url: image,
      width: 1200,
      height: 630
    },
    publisher: {
      "@type": "Organization",
      name: siteName,
      url: baseUrl,
      logo: {
        "@type": "ImageObject",
        url: `${baseUrl}/assets/logo.png`,
        width: 200,
        height: 60
      },
      sameAs: [
        "https://facebook.com/redarauca",
        "https://twitter.com/redarauca",
        "https://instagram.com/redarauca"
      ]
    },
    potentialAction: {
      "@type": "SearchAction",
      target: `${baseUrl}/buscar?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
};